import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { makePickSavingField } from '@helpers/makePickSavingField';

import { apiClient } from '@services/apiClient';

import { type ICostSavingLifetimeKeys, type ICostSavingsLifetime } from '../model/costSavingDetails';

const totalCostSavingLifetimeKey = (keys: ICostSavingLifetimeKeys): [string, Array<number>] => [
  'costSavingsLifetime',
  keys.locationIds,
];

const totalCostSavingsLifetimeFn = ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof totalCostSavingLifetimeKey>, number>) => {
  const params = {
    location_ids: queryKey[1],
  };

  return apiClient.get<{ data: ICostSavingsLifetime }>(
    `${APP_API_URL_V2}fsd/action-item/cost-savings/lifetime`,
    {
      params,
    },
  );
};

const totalCostSavingsLifetimeSelect =
  (locationIds: Array<number>) =>
  ({ data }: AxiosResponse<{ data: ICostSavingsLifetime }>) => {
    const years = data.data.years.map((year) => ({
      value: year,
      label: year,
    }));
    const pick = makePickSavingField(!locationIds);

    const {
      potentialEquipmentLaborSavingCustomerCurrency,
      potentialEquipmentLaborSavingFacilityCurrency,
      potentialSavingsCustomerCurrency,
      potentialSavingsFacilityCurrency,
      totalEquipmentLaborSavingCustomerCurrency,
      totalEquipmentLaborSavingFacilityCurrency,
      totalSavingsCustomerCurrency,
      totalSavingsFacilityCurrency,
      verifiedEquipmentLaborSavingCustomerCurrency,
      verifiedEquipmentLaborSavingFacilityCurrency,
      verifiedSavingsCustomerCurrency,
      verifiedSavingsFacilityCurrency,
      ...rest
    } = data?.data;

    const savings = {
      potentialEquipmentLaborSaving: pick(
        potentialEquipmentLaborSavingCustomerCurrency,
        potentialEquipmentLaborSavingFacilityCurrency,
      ),
      potentialSavings: pick(potentialSavingsCustomerCurrency, potentialSavingsFacilityCurrency),
      totalEquipmentLaborSaving: pick(
        totalEquipmentLaborSavingCustomerCurrency,
        totalEquipmentLaborSavingFacilityCurrency,
      ),
      totalSavings: pick(totalSavingsCustomerCurrency, totalSavingsFacilityCurrency),
      verifiedEquipmentLaborSaving: pick(
        verifiedEquipmentLaborSavingCustomerCurrency,
        verifiedEquipmentLaborSavingFacilityCurrency,
      ),
      verifiedSavings: pick(verifiedSavingsCustomerCurrency, verifiedSavingsFacilityCurrency),
    };

    return { ...rest, ...savings, years };
  };

export const useCostSavingsLifetimeQuery = (keys: ICostSavingLifetimeKeys) => {
  return useQuery({
    queryKey: totalCostSavingLifetimeKey(keys),
    queryFn: totalCostSavingsLifetimeFn,
    select: totalCostSavingsLifetimeSelect(keys.locationIds),
  });
};
